import ActionViewConstructor, { ActionView } from "csi/action/ActionView";
import StateActionController from "csi/breadcrumb/StateActionController";
import config from "csi/config";
import def from "csi/def";
import { ViewRef } from "csi/def/ViewRef";
import html from "csi/html";
import locale from "csi/locale";
import reflection from "csi/reflection";
import { setLocationRevisionUrl } from "csi/router";
import { TableView } from "csi/table/View";
import xml from "csi/xml";
import { SaveDialogProps } from "designSystem/components/dialog/SaveDialog";
import { ButtonEnum } from "designSystem/types/button-types";
import declare from "dojo/_base/declare";

const MassUpdateDataSheetsController = declare(StateActionController, {

	/** @property {boolean} bRefreshRevision - refresh the page to show new revision state */
	bRefreshRevision: false,

	/**
	 * @override
	 * Return MassUpdateDataSheetsTable if view exists on parent node type
	 */
	getActionView(vr: ViewRef): ActionView {
		const parentType = this.getParent().$Type;
		const rc = reflection.getClass(parentType);
		var defView = def.getClassViewDef(rc, "MassUpdateDataSheetsTable") as TableView;

		if (!defView) {
			throw new Error(locale.resource("ErrorNoMassUpdateDataSheetsTable", [parentType]));
		}

		// change heading name based on product type
		const applicableForType = config.getSubtypeSetting(this.getParent().Subtype, "ApplicableForType").toString();
		const productType = xml.enumGetText(applicableForType);
		var heading = defView.findHeadingBy((h: { m_name: string }) => h.m_name === "Product");
		heading.m_name = locale.resource(productType);

		return new ActionViewConstructor(this, defView, this.getParent());
	},

	/**
	 * @override
	 * Return title for popup view
	 */
	getTitle(): string {
		return locale.tooltip(this.m_action.m_id, [locale.typePlural(this.getParent().$Type)]);
	},

	/**
     * @override
     * Add text to the dialog
     * @param {ViewWidget} [widget]
     * @return {Partial<SaveDialogProps>}
     */
	getReactSaveDialogProps(widget): Partial<SaveDialogProps>{
		const originalProps = this.inherited<Partial<SaveDialogProps>>(arguments);

		return {
			...originalProps,
			customOverflowTextProps: {
				res: "MassUpdateDataSheets_Detail",
				resParams: [locale.typePlural(this.getParent().$Type)],
			},
		};
	},

	/**
	 * @override
	 * Change button text for popup view
	 */
	addProxyActions: function(defView, defActions) {
		// add actions for popup view - they will replace that of the current view
		if (!defActions.hasActions()) {
			defActions.addAction(this.getProxyAction(ButtonEnum.BUTTON_UPDATE, true, { m_bSubmit: true }));
		}

		const cancelButton = this.bRefreshRevision ? ButtonEnum.BUTTON_SKIP : ButtonEnum.BUTTON_CANCEL;
		defActions.addAction(this.getProxyAction(cancelButton, false, { m_class: html.CSS_BUTTON_LINK, m_tag: "Cancel" }));
	},

	/**
	 * @override
	 * Navigate to new revision if state changed
	 */
	postProcessSuccess(): void {
		if (this.bRefreshRevision) {
			this.inherited(arguments);
		}
	},

	/**
	 * @override
	 * Navigate to new revision if state changed
	 */
	postProcessCancel() {
		if (this.bRefreshRevision) {
			const master = this.getParent();
			const revisionUrl = master.Revisions.last();
			return setLocationRevisionUrl(revisionUrl);
		}
	},
});

export = MassUpdateDataSheetsController;